import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Globe,
  Heart,
  Headphones,
  Home,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Phone,
  Search,
  ShieldCheck,
  ShoppingBag,
  User,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import { AuthDialog, type AuthDialogMode } from '@/components/auth/AuthDialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SiteLogo } from '@/components/common/SiteLogo';
import { WhatsAppButton } from '@/components/user/WhatsAppButton';
import { useCountries } from '@/hooks/queries/useCountries';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useSiteName } from '@/hooks/useSiteName';
import { useContactInfo } from '@/hooks/useContactInfo';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/utils/apiError';
import { getLocalizedText } from '@/utils/i18n';
import { ACCOUNT_NAV_ITEMS } from '@/config/accountNavigation';

const LANGUAGES = [
  { code: 'zh', label: '中文' },
  { code: 'en', label: 'English' },
  { code: 'ms', label: 'Bahasa Melayu' },
];

const TRAVELER_TYPE_NAV = [
  { identifier: 'family', icon: Users, labelKey: 'nav.travelerFamily' },
  { identifier: 'solo', icon: UserCheck, labelKey: 'nav.travelerSolo' },
  { identifier: 'senior', icon: User, labelKey: 'nav.travelerSenior' },
];

const BOTTOM_NAV_ITEMS = [
  { path: '/', icon: Home, labelKey: 'nav.home' },
  { path: '/search', icon: Search, labelKey: 'nav.search' },
  { path: '/favorites', icon: Heart, labelKey: 'nav.favorites' },
  { path: '/orders', icon: ShoppingBag, labelKey: 'nav.orders' },
  { path: '/profile', icon: User, labelKey: 'nav.profile' },
];

const FOOTER_SOCIALS = [
  { label: 'Instagram', href: 'https://instagram.com/#' },
  { label: 'Facebook', href: 'https://facebook.com/#' },
  { label: 'X (Twitter)', href: 'https://twitter.com/#' },
  { label: 'YouTube', href: 'https://youtube.com/#' },
];

export default function UserLayout() {
  const { lang, setLang, t } = useI18n();
  const { toast } = useToast();
  const siteName = useSiteName();
  const contact = useContactInfo();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authDialogMode, setAuthDialogMode] = useState<AuthDialogMode | null>(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const mainContentRef = useRef<HTMLElement>(null);

  const { data: countriesData } = useCountries({ status: 'active' });
  const countries = countriesData?.items ?? [];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleResize = () => {
      setIsKeyboardVisible(window.innerHeight - viewport.height > 150);
    };
    viewport.addEventListener('resize', handleResize);
    return () => viewport.removeEventListener('resize', handleResize);
  }, []);

  const currentLocationKey = `${location.pathname}${location.search}`;
  const previousLocationRef = useRef(currentLocationKey);
  useEffect(() => {
    if (previousLocationRef.current !== currentLocationKey) {
      previousLocationRef.current = currentLocationKey;
      setMobileMenuOpen(false);
    }
  }, [currentLocationKey]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      setMobileMenuOpen(false);
      navigate('/');
    } catch (error: unknown) {
      toast({
        title: getApiErrorMessage(error, t('auth.logoutFailed')),
        variant: 'destructive',
      });
    }
  }, [logout, navigate, t, toast]);

  const handleAuthLinkClick = (
    event: MouseEvent<HTMLAnchorElement>,
    mode: AuthDialogMode,
  ) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    setMobileMenuOpen(false);
    setAuthDialogMode(mode);
  };

  const handleAuthSuccess = () => {
    setAuthDialogMode(null);
    requestAnimationFrame(() => mainContentRef.current?.focus());
  };

  const runSearch = (value: string) => {
    const query = value.trim();
    setMobileMenuOpen(false);
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search');
  };

  const isNavActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const hideMobileBottomNav =
    location.pathname.startsWith('/packages/') ||
    location.pathname.startsWith('/orders/confirm');

  const navLinkClass = (path: string) =>
    cn(
      'relative px-1 py-2 text-sm font-semibold transition-colors after:absolute after:inset-x-1 after:-bottom-0.5 after:h-0.5 after:origin-left after:rounded-full after:bg-primary after:transition-transform',
      isNavActive(path)
        ? 'text-primary after:scale-x-100'
        : 'text-foreground/70 after:scale-x-0 hover:text-foreground hover:after:scale-x-100',
    );

  return (
    <div className="min-h-screen bg-background">
      <header
        className={cn(
          'sticky top-0 z-50 border-b border-transparent bg-white/95 backdrop-blur-xl transition-all duration-300',
          isScrolled && 'border-border/70 shadow-sm',
        )}
      >
        <div className="hidden bg-ocean text-white lg:block">
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 flex h-8 items-center justify-between text-[11px] font-medium tracking-wide text-white/75">
            <p className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-primary-300" />
              {t('home.trustSecure')}
            </p>
            <Link to="/contact" className="flex items-center gap-2 transition-colors hover:text-white">
              <Headphones className="h-3.5 w-3.5 text-primary-300" />
              {t('home.trustSupport')}
            </Link>
          </div>
        </div>

        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 hidden h-[76px] items-center gap-7 lg:flex">
          <Link to="/" className="shrink-0" aria-label={t('nav.home')}>
            <SiteLogo imageClassName="h-9 w-auto" className="text-xl font-extrabold text-ocean" />
          </Link>

          <nav className="flex items-center gap-5" aria-label={t('nav.mainAriaLabel')}>
            <Link to="/search" className={navLinkClass('/search')}>
              {t('nav.packages')}
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    'flex items-center gap-1 px-1 py-2 text-sm font-semibold transition-colors hover:text-primary',
                    location.search.includes('dest=') || location.search.includes('country=')
                      ? 'text-primary'
                      : 'text-foreground/70',
                  )}
                >
                  {t('nav.destinations')}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 rounded-xl p-2 shadow-lg">
                {countries.map((country) => (
                  <DropdownMenuItem
                    key={country.id}
                    onClick={() => navigate(`/search?country=${country.iso_code}`)}
                    className="rounded-lg py-2.5"
                  >
                    <MapPin className="mr-2 h-4 w-4 text-primary" />
                    {getLocalizedText(country.name, lang)}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/search')} className="rounded-lg py-2.5 font-medium">
                  <Globe className="mr-2 h-4 w-4 text-primary" />
                  {t('nav.allDestinations')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 px-1 py-2 text-sm font-semibold text-foreground/70 transition-colors hover:text-primary">
                  {t('nav.travelBy')}
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 rounded-xl p-2 shadow-lg">
                {TRAVELER_TYPE_NAV.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={item.identifier}
                      onClick={() => navigate(`/search?tt=${item.identifier}`)}
                      className="rounded-lg py-2.5"
                    >
                      <Icon className="mr-2 h-4 w-4 text-primary" />
                      {t(item.labelKey)}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/articles" className={navLinkClass('/articles')}>
              {t('nav.articles')}
            </Link>
            <Link to="/about" className={navLinkClass('/about')}>
              {t('nav.aboutUs')}
            </Link>
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative hidden xl:block">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                placeholder={t('nav.searchPlaceholder')}
                className="h-10 w-56 rounded-full border border-border bg-muted/60 pl-10 pr-4 text-sm outline-none transition-all placeholder:text-muted-foreground focus:w-64 focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') runSearch(event.currentTarget.value);
                }}
              />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full px-3">
                  <Globe className="h-4 w-4" />
                  <span className="text-xs uppercase">{lang}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl p-2">
                {LANGUAGES.map((language) => (
                  <DropdownMenuItem
                    key={language.code}
                    onClick={() => setLang(language.code)}
                    className={cn('rounded-lg', lang === language.code && 'bg-accent')}
                  >
                    {language.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full border-border/80 bg-white px-3.5">
                    <User className="h-4 w-4" />
                    <span className="max-w-24 truncate">{user?.nickname || t('nav.profile')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 rounded-xl p-2 shadow-lg">
                  <div className="px-2 py-2">
                    <p className="truncate text-sm font-semibold">{user?.nickname || ''}</p>
                    <p className="truncate text-xs text-muted-foreground">{user?.email || ''}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {ACCOUNT_NAV_ITEMS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.path} asChild className="rounded-lg py-2.5">
                        <Link to={item.path}>
                          <Icon className="mr-2 h-4 w-4" />
                          {t(item.labelKey)}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="rounded-lg py-2.5 text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" size="sm" className="rounded-full" asChild>
                  <Link
                    to="/login"
                    onClick={(event) => handleAuthLinkClick(event, 'login')}
                  >
                    {t('nav.login')}
                  </Link>
                </Button>
                <Button size="sm" className="rounded-full px-5 shadow-sm" asChild>
                  <Link
                    to="/register"
                    onClick={(event) => handleAuthLinkClick(event, 'register')}
                  >
                    {t('nav.register')}
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="flex h-16 items-center justify-between px-4 lg:hidden">
          <Link to="/" aria-label={t('nav.home')}>
            <SiteLogo imageClassName="h-8 w-auto" className="text-lg font-extrabold text-ocean" />
          </Link>
          <div className="flex items-center gap-1">
            <button
              className="flex h-10 w-10 items-center justify-center rounded-full text-foreground/70 transition-colors hover:bg-accent hover:text-primary"
              onClick={() => navigate('/search')}
              aria-label={t('nav.search')}
            >
              <Search className="h-5 w-5" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex h-10 w-10 items-center justify-center rounded-full text-foreground/70 transition-colors hover:bg-accent hover:text-primary"
                  aria-label={t('nav.changeLanguage')}
                >
                  <Globe className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl p-2">
                {LANGUAGES.map((language) => (
                  <DropdownMenuItem
                    key={language.code}
                    onClick={() => setLang(language.code)}
                    className={cn('rounded-lg', lang === language.code && 'bg-accent')}
                  >
                    {language.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              className="flex h-10 w-10 items-center justify-center rounded-full bg-ocean text-white transition-colors hover:bg-ocean/90"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label={t('nav.menu')}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div
            className={cn(
              'max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t bg-white px-4 pb-6 pt-4 shadow-lg lg:hidden',
              // Keep the last items (e.g. logout) scrollable above the fixed bottom nav.
              !hideMobileBottomNav && 'pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-6',
            )}
          >
            <div className="relative mb-5">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                placeholder={t('nav.searchPlaceholder')}
                className="h-12 w-full rounded-xl border bg-muted/40 pl-10 pr-4 text-sm outline-none focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') runSearch(event.currentTarget.value);
                }}
              />
            </div>

            <nav className="space-y-1" aria-label={t('nav.mobileAriaLabel')}>
              {[
                { path: '/search', label: t('nav.packages') },
                { path: '/articles', label: t('nav.articles') },
                { path: '/about', label: t('nav.aboutUs') },
                { path: '/contact', label: t('nav.contactUs') },
              ].map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'block rounded-xl px-3 py-3 text-sm font-semibold transition-colors',
                    isNavActive(item.path) ? 'bg-accent text-primary' : 'hover:bg-muted',
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {countries.length > 0 && (
              <div className="mt-5 border-t pt-5">
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  {t('nav.destinations')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {countries.slice(0, 8).map((country) => (
                    <button
                      key={country.id}
                      className="rounded-full border bg-white px-3 py-2 text-xs font-medium transition-colors hover:border-primary hover:text-primary"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate(`/search?country=${country.iso_code}`);
                      }}
                    >
                      {getLocalizedText(country.name, lang)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5 border-t pt-5">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                {t('nav.travelBy')}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {TRAVELER_TYPE_NAV.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.identifier}
                      className="flex flex-col items-center gap-2 rounded-xl border bg-white px-2 py-3 text-xs font-semibold transition-colors hover:border-primary hover:text-primary"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate(`/search?tt=${item.identifier}`);
                      }}
                    >
                      <Icon className="h-4 w-4" />
                      {t(item.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 border-t pt-5">
              {isAuthenticated ? (
                <div className="space-y-1">
                  {ACCOUNT_NAV_ITEMS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-muted"
                      >
                        <Icon className="h-4 w-4 text-primary" />
                        {t(item.labelKey)}
                      </Link>
                    );
                  })}
                  <Button variant="ghost" onClick={handleLogout} className="w-full justify-start rounded-xl px-3 text-destructive">
                    <LogOut className="h-4 w-4" />
                    {t('nav.logout')}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" asChild>
                    <Link
                      to="/login"
                      onClick={(event) => handleAuthLinkClick(event, 'login')}
                    >
                      {t('nav.login')}
                    </Link>
                  </Button>
                  <Button asChild>
                    <Link
                      to="/register"
                      onClick={(event) => handleAuthLinkClick(event, 'register')}
                    >
                      {t('nav.register')}
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main
        ref={mainContentRef}
        tabIndex={-1}
        className={cn(
          'min-h-[70vh] focus:outline-none',
          !hideMobileBottomNav && 'pb-16 md:pb-0',
        )}
      >
        <Outlet />
      </main>

      <footer className={cn('bg-ocean text-white/70', !hideMobileBottomNav && 'pb-16 md:pb-0')}>
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.4fr_0.8fr_0.8fr_1fr] lg:gap-12">
            <div>
              <SiteLogo imageClassName="h-10 w-auto brightness-0 invert" className="text-xl font-extrabold text-white" />
              <p className="mt-5 max-w-sm text-sm leading-7 text-white/65">
                {t('footer.companyDescription')}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {FOOTER_SOCIALS.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-white/70 transition-colors hover:border-primary-300 hover:text-primary-200"
                  >
                    {social.label}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white">{t('footer.company')}</h3>
              <ul className="mt-5 space-y-3 text-sm">
                <li><Link to="/about" className="hover:text-white">{t('nav.aboutUs')}</Link></li>
                <li><Link to="/contact" className="hover:text-white">{t('nav.contactUs')}</Link></li>
                <li><Link to="/articles" className="hover:text-white">{t('footer.blog')}</Link></li>
                <li><Link to="/faq" className="hover:text-white">{t('footer.faq')}</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white">{t('footer.legal')}</h3>
              <ul className="mt-5 space-y-3 text-sm">
                <li><Link to="/terms" className="hover:text-white">{t('footer.termsAndConditions')}</Link></li>
                <li><Link to="/privacy" className="hover:text-white">{t('footer.privacyPolicy')}</Link></li>
                <li><Link to="/search" className="hover:text-white">{t('nav.packages')}</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-white">{t('footer.contactInfo')}</h3>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary-300" />
                  <span className="whitespace-pre-line">{contact.address}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-4 w-4 shrink-0 text-primary-300" />
                  <a href={`mailto:${contact.email}`} className="hover:text-white">{contact.email}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-4 w-4 shrink-0 text-primary-300" />
                  <a href={contact.phoneHref} className="hover:text-white">{contact.phone}</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 text-xs text-white/45 sm:flex-row sm:items-center sm:justify-between">
            <p>© {new Date().getFullYear()} {siteName}. {t('footer.allRightsReserved')}</p>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary-300" />
              <span>{t('home.trustSecure')}</span>
            </div>
          </div>
        </div>
      </footer>

      <div className={cn(hideMobileBottomNav && 'hidden lg:block')}>
        <WhatsAppButton />
      </div>

      {!hideMobileBottomNav && !isKeyboardVisible && (
        <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-border/70 bg-white/95 px-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-1 backdrop-blur-xl md:hidden" aria-label={t('nav.mobileBottomAriaLabel')}>
          <div className="flex items-center justify-around">
            {BOTTOM_NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isNavActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex min-h-12 min-w-12 flex-col items-center justify-center gap-0.5 rounded-lg px-2 text-[10px] font-semibold transition-colors',
                    active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className={cn('h-5 w-5', active && 'stroke-[2.5]')} />
                  <span>{t(item.labelKey)}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}

      <AuthDialog
        open={authDialogMode !== null}
        mode={authDialogMode ?? 'login'}
        redirectTo={`${location.pathname}${location.search}${location.hash}`}
        onAuthSuccess={handleAuthSuccess}
        onModeChange={setAuthDialogMode}
        onOpenChange={(open) => {
          if (!open) setAuthDialogMode(null);
        }}
      />
    </div>
  );
}
